import { actionCreator } from "./Sensors.reducer";
import {
  INodeSensor,
  ISensorComment,
  IMeasuringPoint,
  IMeasuringDeviceAlert,
  ISensorData,
  ICommentToDelete,
  ICommentToUpdate,
  ICommentToAdd,
  SensorWithAlarm,
} from "./types";
import { del, get, post, put } from "../common/infrastructure/requests";
import {
  measuringPointUrl,
  measuringDeviceAlertUrl,
  putCommentUrl,
  deleteCommentUrl,
  addCommentUrl,
  getNodeWithParentsUrl,
  installationsOnDomainUrl,
  measuringpointCommentsUrl,
  installationsOnNodeUrl,
  meterEventsUrl,
  meterEventsSubscriptionsUrl,
} from "../common/infrastructure/endpoints";
import { INodeWithParents } from "../nodeDetails/types";
import { DeviceType } from "../common/types";
import { EventType, getDeviceAlertDescription } from "./deviceAlerts";
import { IMeterEventsObject } from "meter/types";
import moment from "moment";
import { IApplicationState } from "redux/root-reducer";
import { IMeterEventSubscriptionObject } from "meter/types";
import { isLandlordTypeGuard } from "common/models/userInfo";

export const getAllDomainAlarms =
  (domain: string) => async (dispatch: ({}) => void) => {
    dispatch(actionCreator.fetching);
    try {
      const installationsOnDomain = await getAllInstallationsOnDomain(domain);

      if (!installationsOnDomain) {
        dispatch(actionCreator.fetchingCurrentAlarmsSuccess([]));
      }

      const installationsIds = installationsOnDomain.map(
        (s) => s.MeasuringDeviceID
      );
      const alarms = await get<IMeasuringDeviceAlert[]>(
        measuringDeviceAlertUrl(domain, installationsIds)
      );
      if (!alarms) {
        //"No alarms don't proceed"
        dispatch(actionCreator.fetchingCurrentAlarmsSuccess([]));
      }

      let nodesWithAlerts = installationsOnDomain.reduce(
        (nodesWithAlerts, installation) => {
          const alert = alarms.find(
            (mda) => mda.MeasuringDeviceID === installation.MeasuringDeviceID
          );
          if (!alert) {
            return nodesWithAlerts;
          }
          const newNode: SensorWithAlarm = {
            Alert: {
              Id: alert.AlertID,
              Description: getDeviceAlertDescription(alert.AlertID),
            },
            DeviceType: installation.DeviceType,
            MeasuringDeviceID: installation.MeasuringDeviceID,
            MeasuringPointID: installation.MeasuringPointID,
            NodeID: installation.NodeID,
            Address: undefined,
          };

          return [...nodesWithAlerts, newNode];
        },
        [] as SensorWithAlarm[]
      );
      await Promise.all(
        nodesWithAlerts.map((node) => {
          return get<INodeWithParents>(
            getNodeWithParentsUrl(domain, node.NodeID!)
          );
        })
      )
        .then((n) => {
          nodesWithAlerts = nodesWithAlerts.map((nA) => {
            //e.g Sommarvägen 1-5
            const address = n.find((n) => n.ID === nA.NodeID)?.Name ?? "-";
            nA = { ...nA, Address: address };
            return nA;
          });

          dispatch(actionCreator.fetchingCurrentAlarmsSuccess(nodesWithAlerts));
        })
        .catch();
    } catch (e) {
      dispatch(actionCreator.fetchingError);
    }
  };

export const getHistoryForSensor =
  (domain: string, nodeId: number, measuringPointID: number) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    const state = getState();
    const isLandlord = isLandlordTypeGuard(state.common.currentUserInfo);
    const allSensorsforSingleNode = [
      ...state.sensors.allSensorDataForSingleNode,
    ];
    const allSensorDataForSingleNode = allSensorsforSingleNode.find(
      (sensorData) => sensorData.MeasuringPointID === measuringPointID
    );
    if (!allSensorDataForSingleNode) {
      return;
    }

    const oneMonthAgo = moment().subtract(1, "month").unix();

    const eventHistory = await get<IMeterEventsObject[]>(
      meterEventsUrl(domain, nodeId.toString(), isLandlord, oneMonthAgo)
    );
    const filteredHistory = eventHistory.filter(
      (e) =>
        e.PositionID === measuringPointID && e.EventTypeID !== EventType.Alarm
    );
    const alertHistory = filteredHistory.map((event) => {
      return {
        EventType: event.EventType,
        ChangeTime: event.ChangeTime,
        EventTypeID: event.EventTypeID,
        PositionID: event.PositionID,
        PositionName: event.PositionName,
        Value: event.Value,
      } as IMeterEventsObject;
    });
    const newSensor = {
      ...allSensorDataForSingleNode,
      PreviousEvents: alertHistory || [],
    };

    dispatch(actionCreator.fetchingSensorHistorySuccess(newSensor));
  };

export const getAllSensorDataForSingleNode =
  (domain: string, nodeId: number, installations?: INodeSensor[]) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    try {
      dispatch(actionCreator.fetching);

      const { currentUserInfo } = getState().common;
      let installationIds: string[];
      let meterEventsSubscription: IMeterEventSubscriptionObject[];
      if (!isLandlordTypeGuard(currentUserInfo)) {
        meterEventsSubscription = await get<IMeterEventSubscriptionObject[]>(
          meterEventsSubscriptionsUrl(domain)
        );
        if (meterEventsSubscription?.length === 0) {
          dispatch(actionCreator.fetchingError);
          return;
        }
      }

      if (!installations) {
        installations = await get<INodeSensor[]>(
          installationsOnNodeUrl(domain, nodeId, [
            DeviceType.Leakage,
            DeviceType.Smoke,
          ])
        );
        installationIds = installations.map((i) => i.MeasuringDeviceID);
      } else if (installations.length === 0) {
        dispatch(actionCreator.fetchingSensorDataSuccess([]));
        return;
      } else {
        installationIds = installations.map((i) => i.MeasuringDeviceID);
      }
      const adress = (
        await get<INodeWithParents>(getNodeWithParentsUrl(domain, nodeId))
      ).ParentNodes.find((parent) => parent.NodeType.Code === 6)?.Name;
      const measuringPoints = await getMeasuringPoints(domain, nodeId);

      if (measuringPoints?.length === 0) {
        dispatch(actionCreator.fetchingCombinedSensorDataError);
        return;
      }

      const alarms = await get<IMeasuringDeviceAlert[]>(
        measuringDeviceAlertUrl(domain, installationIds)
      );

      const res: ISensorData[] = installations.map((sensor) => {
        const measuringPointName =
          measuringPoints?.find((mp) => mp.ID === sensor.MeasuringPointID)
            ?.Name || "";
        const alert = alarms?.find(
          (mda) => mda.MeasuringDeviceID === sensor.MeasuringDeviceID
        );

        const { Enabled, EventType } =
          meterEventsSubscription?.find(
            (s) => s.PositionID === sensor.MeasuringPointID
          ) || {};

        return {
          Alert: alert
            ? {
                Id: alert.AlertID,
                Description: getDeviceAlertDescription(alert.AlertID) || "",
              }
            : undefined,
          DeviceTypeDisplay: sensor.DeviceTypeDisplay,
          ExternalKey: sensor.ExternalKey,
          DeviceType: sensor.DeviceType,
          MeasuringDeviceID: sensor.MeasuringDeviceID,
          MeasuringPointID: sensor.MeasuringPointID,
          NodeID: sensor.NodeID,
          MeasuringPointName: measuringPointName,
          Address: adress,
          Monitoring: !!Enabled,
          EventType: EventType,
          PreviousEvents: [],
        };
      });
      dispatch(actionCreator.fetchingSensorDataSuccess(res));
    } catch (e) {
      dispatch(actionCreator.fetchingError);
      throw new Error((e as Error).message);
    }
  };

export const getMeasuringPoints = async (domain: string, nodeId: number) => {
  try {
    const result = await get<IMeasuringPoint[]>(
      measuringPointUrl(domain, nodeId)
    );

    if (result.length === 0) {
      return null;
    }
    return result;
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

const getAllInstallationsOnDomain = async (domain: string) => {
  try {
    const result = await get<INodeSensor[]>(
      installationsOnDomainUrl(domain, [DeviceType.Leakage, DeviceType.Smoke])
    );
    if (result.length === 0) {
      throw "no installations found on domain";
    }
    return result;
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const getComments =
  (domain: string, measuringpointId: number) =>
  async (dispatch: ({}) => void) => {
    try {
      const comments = await get<ISensorComment[]>(
        measuringpointCommentsUrl(domain, measuringpointId)
      );

      dispatch(actionCreator.fetchingSensorComments(comments));
    } catch (e) {
      throw new Error((e as Error).message);
    }
  };

export const deleteComment =
  (domain: string, id: number) => async (dispatch: ({}) => void) => {
    dispatch(actionCreator.fetching);
    try {
      const commentToDelete = await del(deleteCommentUrl(domain, id as number));
      if (!commentToDelete) {
        dispatch(actionCreator.fetchingError);
        return;
      }
      dispatch(
        actionCreator.deletingSensorCommentSuccess({ id } as ICommentToDelete)
      );
    } catch (e) {
      dispatch(actionCreator.fetchingError);
      throw new Error((e as Error).message);
    }
  };

export const addComment =
  (domain: string, comment: ICommentToAdd) =>
  async (dispatch: ({}) => void) => {
    dispatch(actionCreator.fetching);
    try {
      const resp = await post<any, ICommentToAdd>(
        addCommentUrl(domain),
        comment
      );
      if (!resp) {
        dispatch(actionCreator.fetchingError);
        return;
      }
      dispatch(actionCreator.addSensorCommentSuccess(resp as ISensorComment));
    } catch (e) {
      dispatch(actionCreator.fetchingError);
      throw new Error((e as Error).message);
    }
  };

export const putComment =
  (domain: string, comment: ICommentToUpdate) =>
  async (dispatch: ({}) => void) => {
    dispatch(actionCreator.fetching);
    try {
      const resp = await put<any, ICommentToUpdate>(
        putCommentUrl(domain, comment.Id),
        comment
      );
      if (!resp) {
        dispatch(actionCreator.fetchingError);
        return;
      }
      dispatch(
        actionCreator.putSensorCommentSuccess(comment as ICommentToUpdate)
      );
    } catch (e) {
      dispatch(actionCreator.fetchingError);
      throw new Error((e as Error).message);
    }
  };

export const toggleAllSensorsAction =
  (domainCode: string, toggle: boolean) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    const { allSensorDataForSingleNode } = getState().sensors;
    const safetySensorsIds: number[] = allSensorDataForSingleNode.map(
      (sensor) => sensor.MeasuringPointID
    );

    try {
      const currentSubscriptionsRes = await get<
        IMeterEventSubscriptionObject[]
      >(meterEventsSubscriptionsUrl(domainCode));
      const body = currentSubscriptionsRes
        .filter((sub) => safetySensorsIds.includes(sub.PositionID))
        .map((sub) => {
          return { ...sub, Enabled: toggle };
        });

      const resp = await put<any, any[]>(
        meterEventsSubscriptionsUrl(domainCode),
        body
      );

      if (!resp) {
        dispatch(actionCreator.fetchingError);
        return;
      }

      dispatch(actionCreator.toggleMonitoringForAllSensors(toggle));
    } catch {
      dispatch(actionCreator.fetchingError);
    }
  };

export const toggleSingleSensorAction =
  (domainCode: string, sensor: ISensorData, toggle: boolean) =>
  async (dispatch: ({}) => void) => {
    try {
      const currentSubscriptionsRes = await get<
        IMeterEventSubscriptionObject[]
      >(meterEventsSubscriptionsUrl(domainCode));
      const body = currentSubscriptionsRes.map((sub) => {
        if (sub.PositionID === sensor.MeasuringPointID) {
          const meterSub: IMeterEventSubscriptionObject = {
            PositionID: sub.PositionID,
            PositionName: sub.PositionName,
            EventType: sub.EventType,
            EventTypeID: sub.EventTypeID,
            Enabled: toggle,
          };
          return meterSub;
        }
        return sub;
      });

      const res = await put<null, IMeterEventSubscriptionObject[]>(
        meterEventsSubscriptionsUrl(domainCode),
        body
      );

      if (!res) {
        dispatch(actionCreator.fetchingError);
        return;
      }

      dispatch(
        actionCreator.toggleMonitoringForSingleSensor({
          ...sensor,
          Monitoring: toggle,
        })
      );
    } catch {
      console.error("Could not toggle single sensor");
    }
  };

export const generateSensorItems = (
  allSensors: ISensorData[],
  checked: boolean
): IMeterEventSubscriptionObject[] =>
  allSensors.map((s) => ({
    PositionID: s.MeasuringPointID,
    EventType: s.EventType || "",
    Enabled: checked,
  }));
